import { tz } from "@date-fns/tz"
import {
  endOfDay,
  formatISO,
  isBefore,
  isSameDay,
  parseISO,
  startOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  subYears,
  addDays,
} from "date-fns"
import { Timestamp as TimestampPB } from "gen/google/protobuf/timestamp_pb"
import {
  TimestampRange as TimestampRangePB,
  TimestampRange_RelativeType as RelativeType,
} from "gen/proto/timestamprange/models_pb"

import { Format, Timestamp } from "../../classes/timestamp"

export class TimestampRange {
  static humanize(
    tr?: TimestampRangePB,
    {
      timeZone,
      format: f = Format.DATE,
    }: { timeZone?: string; format?: Format } = {}
  ): string {
    if (!tr) {
      return ""
    }

    if (!tr.start || !tr.end) {
      return ""
    }

    const opts = timeZone ? { in: tz(timeZone) } : undefined
    if (
      isSameDay(TimestampPB.toDate(tr.start), TimestampPB.toDate(tr.end), opts)
    ) {
      return Timestamp.format(f, tr.end, timeZone)
    }

    return `${Timestamp.format(f, tr.start, timeZone)} - ${Timestamp.format(f, tr.end, timeZone)}`
  }

  static toQueryParam(tr: TimestampRangePB | null) {
    if (!tr) {
      return ""
    }

    if (!tr.start || !tr.end) {
      return ""
    }

    if (isSameDay(TimestampPB.toDate(tr.start), TimestampPB.toDate(tr.end))) {
      return formatISO(TimestampPB.toDate(tr.start))
    }

    return `${formatISO(TimestampPB.toDate(tr.start))}/${formatISO(
      TimestampPB.toDate(tr.end)
    )}`
  }

  static fromQueryParam(qp?: string | string[]): TimestampRangePB | null {
    if (!qp) {
      return null
    }

    if (typeof qp !== "string") {
      return null
    }

    const [start, end] = qp.split("/")

    if (!start && !end) {
      return null
    }

    if (start && !end) {
      return TimestampRangePB.create({
        start: TimestampPB.fromDate(startOfDay(parseISO(start))),
        end: TimestampPB.fromDate(endOfDay(parseISO(start))),
      })
    }

    return TimestampRangePB.create({
      start: TimestampPB.fromDate(parseISO(start)),
      end: TimestampPB.fromDate(parseISO(end)),
    })
  }

  // fromPartialQueryParam reads from a query param string `qp` that
  // may have incomplete start/end dates. While we are in the process of
  // moving to a future where query params hold all filter information, this
  // function should be used for all future "dates inside query params" work
  static fromPartialQueryParam(
    dateParam: string | string[] | undefined
  ): TimestampRangePB | null {
    if (!dateParam || typeof dateParam !== "string") {
      return null
    }

    const [startStr, endStr] = decodeURIComponent(dateParam).split("/")

    if (!startStr && !endStr) {
      return null
    }

    const start = startStr
      ? TimestampPB.fromDate(parseISO(startStr))
      : undefined
    const end = endStr ? TimestampPB.fromDate(parseISO(endStr)) : undefined

    return TimestampRangePB.create({
      start,
      end,
    })
  }

  // toPartialQueryParam generates a query param string that may have
  // incomplete start/end dates. While we are in the process of moving
  // to a future where query params hold all filter information, this
  // function should be used for all future "dates inside query params" work
  static toPartialQueryParam(tr: TimestampRangePB | null) {
    if (!tr?.start && !tr?.end) {
      return ""
    }

    const start = tr.start ? formatISO(TimestampPB.toDate(tr.start)) : ""
    const end = tr.end ? formatISO(TimestampPB.toDate(tr.end)) : ""

    return encodeURIComponent(`${start}/${end}`)
  }

  static isValid(tr: TimestampRangePB | null): boolean {
    if (!tr) {
      return false
    }

    if (!tr.start || !tr.end) {
      return false
    }

    return isBefore(TimestampPB.toDate(tr.start), TimestampPB.toDate(tr.end))
  }

  // Resolve converts a relative TimestampRange into an absolute one (with start and end timestamps).
  // If the TimestampRange is already absolute, it returns a copy with just start/end.
  static resolve(tr: TimestampRangePB | null): TimestampRangePB | null {
    if (!tr) {
      return null
    }

    if (tr.relativeType === RelativeType.UNDEFINED) {
      return TimestampRangePB.create({
        start: tr.start,
        end: tr.end,
      })
    }

    const now = new Date()
    const today = startOfDay(now)

    let start: Date
    let end: Date

    switch (tr.relativeType) {
      case RelativeType.RANGE_N_DAYS:
        start = addDays(today, -tr.relativeStart)
        end = addDays(today, -tr.relativeEnd)
        end = endOfDay(end)
        break
      case RelativeType.RANGE_N_WEEKS:
        start = addDays(today, -tr.relativeStart * 7)
        end = addDays(today, -tr.relativeEnd * 7)
        end = endOfDay(end)
        break
      case RelativeType.RANGE_N_MONTHS:
        start = subMonths(today, tr.relativeStart)
        end = subMonths(today, tr.relativeEnd)
        end = endOfDay(end)
        break
      case RelativeType.RANGE_N_YEARS:
        start = subYears(today, tr.relativeStart)
        end = subYears(today, tr.relativeEnd)
        end = endOfDay(end)
        break
      case RelativeType.TODAY:
        start = today
        end = endOfDay(today)
        break
      case RelativeType.YESTERDAY:
        start = addDays(today, -1)
        end = endOfDay(addDays(today, -1))
        break
      case RelativeType.LAST_WEEK:
        start = startOfWeek(subWeeks(today, 1))
        end = endOfWeek(subWeeks(today, 1))
        break
      case RelativeType.LAST_MONTH:
        start = startOfMonth(subMonths(today, 1))
        end = endOfMonth(subMonths(today, 1))
        break
      case RelativeType.LAST_YEAR:
        start = startOfYear(subYears(today, 1))
        end = endOfYear(subYears(today, 1))
        break
      case RelativeType.WEEK_TO_DATE:
        start = startOfWeek(today)
        end = endOfDay(today)
        break
      case RelativeType.MONTH_TO_DATE:
        start = startOfMonth(today)
        end = endOfDay(today)
        break
      case RelativeType.YEAR_TO_DATE:
        start = startOfYear(today)
        end = endOfDay(today)
        break
      case RelativeType.ALL_TIME:
        start = new Date(0)
        end = endOfDay(today)
        break
      default:
        return null
    }

    return TimestampRangePB.create({
      start: TimestampPB.fromDate(start),
      end: TimestampPB.fromDate(end),
    })
  }
}
